import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Circle, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import { serviceCities, serviceCenter, ServiceCity } from '../data/serviceAreas';
import { company } from '../data/content';
import { PhoneIcon, ArrowRightIcon, WaveIcon } from './icons';
function MapFocus({
  city
}: {
  city: ServiceCity | null;
}) {
  const map = useMap();
  useEffect(() => {
    if (city) {
      map.flyTo([city.lat, city.lng], 11, {
        duration: 0.6
      });
    } else {
      map.flyTo([serviceCenter.lat, serviceCenter.lng], 9, {
        duration: 0.6
      });
    }
  }, [city, map]);
  return null;
}
export function ServiceArea() {
  const [activeCity, setActiveCity] = useState<ServiceCity | null>(null);
  return <section id="service-area" className="w-full bg-white py-20 lg:py-28" data-id="element-581" data-mp="16yuhgyfxqic9">
      <div className="mx-auto max-w-content px-5 lg:px-8" data-id="element-582" data-mp="3cscay7thuwo3">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,4fr)_minmax(0,7fr)] lg:gap-14" data-id="element-583" data-mp="38pphy3l375pb">
          <div data-id="element-584" data-mp="38s24ku1si48p">
            <p className="text-sm font-semibold text-sky-600" data-id="element-585" data-mp="1a88fdml7oaxz">Where we work</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-navy-800 sm:text-4xl" data-id="element-586" data-mp="1a641cbtllvjz">
              Serving Greater Houston, one backyard at a time.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-navy-500" data-id="element-587" data-mp="1owur7ebmqkkg">
              We stay local on purpose — close enough to show up when we say we will, and close
              enough to come back if you ever need us. Tap a city to see it on the map.
            </p>

            <ul className="mt-7 flex flex-wrap gap-2" data-id="element-588" data-mp="1xzm7brfga4if">
              {serviceCities.map(city => {
              const isActive = activeCity?.name === city.name;
              return <li key={city.name} data-id="element-589" data-mp="27uxmgaurtdqp">
                    <button type="button" onClick={() => setActiveCity(isActive ? null : city)} aria-pressed={isActive} className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400 ${isActive ? 'border-navy-800 bg-navy-800 text-white' : 'border-navy-200 text-navy-600 hover:border-navy-400'}`} data-id="element-590" data-mp="8iuvygnqbj3j">
                      {city.name}
                    </button>
                  </li>;
            })}
            </ul>

            <div className="mt-8 flex flex-col gap-3 border-t border-navy-100 pt-7 sm:flex-row sm:items-center" data-id="element-591" data-mp="y5nua9mvj7ou">
              <p className="text-[15px] leading-relaxed text-navy-500" data-id="element-592" data-mp="t62kf4661exc">
                Don&apos;t see your city? We travel for the right project — just ask.
              </p>
              <a href={company.phoneHref} className="inline-flex shrink-0 items-center gap-2 font-display text-[15px] font-bold text-sky-600 transition-colors duration-150 ease-out hover:text-sky-500" data-id="element-593" data-mp="181h3bj1dwj1v">
                <PhoneIcon className="h-4 w-4" data-id="element-594" data-mp="1qr41ng5eaaax" />
                {company.phoneDisplay}
                <ArrowRightIcon className="h-4 w-4" data-id="element-595" data-mp="1ct5qskqa8w06" />
              </a>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-navy-100" data-id="element-596" data-mp="1hqecuwejxk6p">
            <div className="h-[380px] w-full sm:h-[460px]" data-id="element-597" data-mp="1321e5ro2xn8i">
              <MapContainer center={[serviceCenter.lat, serviceCenter.lng]} zoom={9} scrollWheelZoom={false} className="h-full w-full" aria-label="Map of A&J Pool Plaster service area across Greater Houston" data-id="element-598" data-mp="130gk035og6b6">
                <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>' data-id="element-599" data-mp="yylioynqxsxe" />
                <Circle center={[serviceCenter.lat, serviceCenter.lng]} radius={serviceCenter.radiusMeters} pathOptions={{
                color: '#1E93CC',
                weight: 1.5,
                fillColor: '#3FAFE2',
                fillOpacity: 0.12
              }} data-id="element-600" data-mp="1sfscwdzrimnh" />
                {serviceCities.map(city => {
                const isActive = activeCity?.name === city.name;
                return <CircleMarker key={city.name} center={[city.lat, city.lng]} radius={isActive ? 11 : city.featured ? 9 : 7} pathOptions={{
                  color: '#0A1F36',
                  weight: 2,
                  fillColor: isActive ? '#FFD700' : '#3FAFE2',
                  fillOpacity: 1
                }} eventHandlers={{
                  click: () => setActiveCity(isActive ? null : city)
                }} data-id="element-601" data-mp="zp0h3hmm000o">
                      <Tooltip direction="top" offset={[0, -6]} data-id="element-602" data-mp="36daiqr0kslnk">
                        <span className="font-semibold" data-id="element-603" data-mp="36e2go8hnavh0">{city.name}</span> · {city.county}
                      </Tooltip>
                    </CircleMarker>;
              })}
                <MapFocus city={activeCity} data-id="element-604" data-mp="2dq6dckxkm5b9" />
              </MapContainer>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-navy-100 bg-sand px-5 py-4" data-id="element-605" data-mp="2iohmetg1kbsf">
              <span className="flex items-center gap-2 text-[13px] font-medium text-navy-600" data-id="element-606" data-mp="2nlhsh4cw5ejj">
                <span className="h-3 w-3 rounded-full border-2 border-navy-800 bg-sky-400" aria-hidden="true" data-id="element-607" data-mp="2sn74onbuny97" />
                Cities we serve
              </span>
              <span className="flex items-center gap-2 text-[13px] font-medium text-navy-600" data-id="element-608" data-mp="7xhreeqsokim">
                <span className="h-3 w-3 rounded-full border border-sky-500 bg-sky-400/25" aria-hidden="true" data-id="element-609" data-mp="240w81fr4l3xa" />
                ~40 mile service radius
              </span>
              <span className="ml-auto flex items-center gap-2 text-[13px] font-medium text-navy-500" data-id="element-610" data-mp="28y5vipuyf5ih">
                <WaveIcon className="h-4 w-4 text-sky-500" data-id="element-611" data-mp="1l8221bbhcm57" />
                Plaster · Tile · Coping · Spray decks · Remodels
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}